import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';
import { tap, withLatestFrom } from 'rxjs/operators';
import { AutoSavePersistService } from 'common/auto-save/auto-save-persist.service';
import { WrapUpCode } from 'common/store/genesys-cloud/conversations/common/models/wrap-up-code.model';
import {
  getEmailDraftStorageKey,
  getEmailFooterModeStorageKey
} from 'common/store/genesys-cloud/conversations/emails/helpers/email-drafts.helpers';
import { EmailMessage } from 'common/store/genesys-cloud/conversations/emails/models/email-message.model';
import { EmailBoxFooterMode } from './models/email-box-footer-mode.type';

interface FooterModeModel {
  footerMode: EmailBoxFooterMode;
  previousFooterMode: EmailBoxFooterMode;
}

export interface EmailFooterState {
  footerMode: EmailBoxFooterMode;
  isMoreActionsVisible: boolean;
  editorMessages: { message: string; translateParams: any }[];
  composedEmail: Partial<EmailMessage>;
  isInteractionsEnabled: boolean;
  isConfirmationModalVisible: boolean;
  hasValidEmailRecipients: boolean;
  isQuotedMessageAdded: boolean;
  conversationId: string;
  previousFooterMode: EmailBoxFooterMode;
  selectedWrapUpCode: WrapUpCode | null;
  emailDraftStorageKey: string;
  emailFooterModeStorageKey: string;
}

export const initialState: EmailFooterState = {
  footerMode: 'CONTROL_PANEL',
  previousFooterMode: 'CONTROL_PANEL',
  isMoreActionsVisible: false,
  editorMessages: [],
  isInteractionsEnabled: true,
  isConfirmationModalVisible: false,
  hasValidEmailRecipients: true,
  isQuotedMessageAdded: false,
  selectedWrapUpCode: null,
  conversationId: '',
  emailFooterModeStorageKey: '',
  emailDraftStorageKey: '',
  composedEmail: {
    from: null,
    to: [],
    cc: [],
    bcc: [],
    subject: '',
    textBody: '',
    htmlBody: ''
  }
};

@Injectable()
export class GenesysEmailFooterStore extends ComponentStore<EmailFooterState> {
  constructor(private readonly _autoSavePersistService: AutoSavePersistService) {
    super(initialState);
  }

  readonly footerMode$ = this.select((state) => state.footerMode);

  readonly isMoreActionsVisible$ = this.select((state) => state.isMoreActionsVisible);

  readonly editorMessages$ = this.select((state) => state.editorMessages);

  readonly composedEmail$ = this.select((state) => state.composedEmail);

  readonly isInteractionsEnabled$ = this.select((state) => state.isInteractionsEnabled);

  readonly isConfirmationModalVisible$ = this.select((state) => state.isConfirmationModalVisible);

  readonly hasValidEmailRecipients$ = this.select((state) => state.hasValidEmailRecipients);

  readonly isQuotedMessageAdded$ = this.select((state) => state.isQuotedMessageAdded);

  readonly selectedWrapUpCode$ = this.select((state) => state.selectedWrapUpCode);

  readonly storeFooterMode = this.effect((footerMode$: Observable<EmailBoxFooterMode>) =>
    footerMode$.pipe(
      withLatestFrom(this.state$),
      tap(([footerMode, state]) => {
        const newFooterMode = {
          footerMode: footerMode,
          previousFooterMode: state.footerMode
        };
        this._persistAndUpdateFooterMode(state.emailFooterModeStorageKey, newFooterMode);
      })
    )
  );

  readonly cancelFooterMode = this.effect((action$: Observable<void>) =>
    action$.pipe(
      withLatestFrom(this.state$),
      tap(([_, state]) => {
        const newFooterMode = {
          footerMode: state.previousFooterMode,
          previousFooterMode: state.footerMode
        };
        this._persistAndUpdateFooterMode(state.emailFooterModeStorageKey, newFooterMode);
      })
    )
  );

  readonly initFooterMode = this.effect((footerMode$: Observable<EmailBoxFooterMode | null>) =>
    footerMode$.pipe(
      withLatestFrom(this.state$),
      tap(([footerMode, state]) => {
        const storedFooterMode = this._getPersistedFooterMode(state.emailFooterModeStorageKey);
        const initFooterMode = footerMode || storedFooterMode?.footerMode || 'CONTROL_PANEL';

        const newFooterMode = {
          footerMode: initFooterMode,
          previousFooterMode: storedFooterMode?.previousFooterMode ?? 'CONTROL_PANEL'
        };
        this._persistAndUpdateFooterMode(state.emailFooterModeStorageKey, newFooterMode);
      })
    )
  );

  readonly storeComposedEmail = this.effect((composedEmail$: Observable<Partial<EmailMessage>>) =>
    composedEmail$.pipe(
      withLatestFrom(this.state$),
      tap(([composedEmail, state]) => {
        const updatedComposedEmail = { ...state.composedEmail, ...composedEmail };

        this._persistAndUpdateComposedEmail(updatedComposedEmail, state.emailDraftStorageKey);
      })
    )
  );

  readonly initComposedEmail = this.effect((composedEmail$: Observable<Partial<EmailMessage>>) =>
    composedEmail$.pipe(
      withLatestFrom(this.state$),
      tap(([composedEmail, state]) => {
        const savedComposedEmail = this._autoSavePersistService.load(
          state.emailDraftStorageKey
        ) as Partial<EmailMessage>;
        const initComposedEmail = savedComposedEmail || composedEmail;

        this._persistAndUpdateComposedEmail(initComposedEmail, state.emailDraftStorageKey);
      })
    )
  );

  readonly resetState = this.effect((conversationId$: Observable<string>) =>
    conversationId$.pipe(
      withLatestFrom(this.state$),
      tap(([conversationId, state]) => {
        this._autoSavePersistService.clear(state.emailDraftStorageKey);
        this._autoSavePersistService.clear(state.emailFooterModeStorageKey);

        this.setState({ ...initialState });
        this.initConversationId(conversationId);
      })
    )
  );

  initConversationId(conversationId: string) {
    const emailFooterModeStorageKey = getEmailFooterModeStorageKey(conversationId);
    const emailDraftStorageKey = getEmailDraftStorageKey(conversationId);
    this.patchState({ conversationId, emailFooterModeStorageKey, emailDraftStorageKey });
  }

  setInteractionsEnabled(isInteractionsEnabled: boolean): void {
    this.patchState({ isInteractionsEnabled });
  }

  setHasValidEmailRecipients(hasValidEmailRecipients: boolean): void {
    this.patchState({ hasValidEmailRecipients });
  }

  setIsQuotedMessageAdded(isQuotedMessageAdded: boolean): void {
    this.patchState({ isQuotedMessageAdded });
  }

  toggleMoreActionsVisible(): void {
    this.patchState(({ isMoreActionsVisible }) => ({ isMoreActionsVisible: !isMoreActionsVisible }));
  }

  addEditorMessage(message: string, translateParams?: any): void {
    this.patchState(({ editorMessages }) => {
      return { editorMessages: [...editorMessages, { message, translateParams }] };
    });
  }

  setEditorMessages(message: string, translateParams?: any): void {
    this.patchState({ editorMessages: [{ message, translateParams }] });
  }

  removeEditorMessage(message: string): void {
    this.patchState(({ editorMessages }) => {
      return { editorMessages: editorMessages.filter((editorMessage) => editorMessage.message !== message) };
    });
  }

  clearEditorMessages(): void {
    this.patchState({ editorMessages: [] });
  }

  setSelectedWrapUpCode(wrapUpCode: WrapUpCode): void {
    this.patchState({ selectedWrapUpCode: wrapUpCode });
  }

  setIsConfirmationModalVisible(isConfirmationModalVisible: boolean): void {
    this.patchState({ isConfirmationModalVisible });
  }

  private _persistAndUpdateComposedEmail(
    updatedComposedEmail: Partial<EmailMessage>,
    emailDraftStorageKey: string
  ): void {
    this._autoSavePersistService.save(emailDraftStorageKey, updatedComposedEmail);
    this.patchState(({ composedEmail }) => ({
      composedEmail: { ...composedEmail, ...updatedComposedEmail }
    }));
  }

  private _getPersistedFooterMode(emailFooterModeStorageKey: string): FooterModeModel {
    const storedFooterMode = this._autoSavePersistService.load(emailFooterModeStorageKey) as FooterModeModel;
    return storedFooterMode;
  }

  private _persistAndUpdateFooterMode(emailFooterModeStorageKey: string, footerMode: FooterModeModel): void {
    this._autoSavePersistService.save(emailFooterModeStorageKey, footerMode);

    this.patchState({
      ...footerMode
    });
  }
}
